import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import html2canvas from "html2canvas";
import { v4 as uuidv4 } from "uuid";

// Google Form constants (from user's prefilled link)
const FORM_ACTION =
  "https://docs.google.com/forms/d/e/1FAIpQLSdnIbYwXep99MTGL_70crA0KdTYeOLtfEbYCMsH3CAUesO2IQ/formResponse";
const ENTRY_MAP = {
  id: "entry.734036304",
  email: "entry.1620088191",
  pattern_name: "entry.317815133",
  creator_name: "entry.1393190314",
  image_url: "entry.1658323810",
  chart_json: "entry.1234308792",
  instructions: "entry.899447527",
  tags: "entry.1243172311",
  approval_status: "entry.719890838",
  created_at: "entry.239739135",
  notes: "entry.382138115",
};

function toBase64Unicode(jsonString) {
  try {
    return btoa(unescape(encodeURIComponent(jsonString)));
  } catch (e) {
    return btoa(jsonString);
  }
}

const SubmitToGalleryModal = ({
  open,
  onClose,
  chart,
  patternTitle,
  compiledInstructions,
}) => {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [tags, setTags] = useState("");
  const [notes, setNotes] = useState("");
  const [title, setTitle] = useState(patternTitle || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const generatedId = useMemo(() => uuidv4(), []);

  async function captureThumbnail() {
    // Capture the chart grid element (same id used by Export flow)
    const el = document.getElementById("chart-grid");
    if (!el) return "";

    const canvas = await html2canvas(el, {
      backgroundColor: "#ffffff",
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      width: el.scrollWidth,
      height: el.scrollHeight,
    });

    // Resize to square thumbnail (max 256x256) and encode as JPEG to keep size modest
    const maxSide = 256;
    const ratio = Math.min(maxSide / canvas.width, maxSide / canvas.height, 1);
    const outW = Math.floor(canvas.width * ratio);
    const outH = Math.floor(canvas.height * ratio);
    const out = document.createElement("canvas");
    out.width = outW;
    out.height = outH;
    const ctx = out.getContext("2d");
    ctx.drawImage(canvas, 0, 0, outW, outH);
    return out.toDataURL("image/jpeg", 0.7);
  }

  async function handleSubmit() {
    try {
      setError("");
      if (!chart) {
        setError("No chart to submit.");
        return;
      }

      setIsSubmitting(true);

      const imageDataUrl = await captureThumbnail();
      const chartJsonBase64 = toBase64Unicode(JSON.stringify(chart));
      const titleToUse = (title || patternTitle || "Untitled Pattern").trim();
      const creatorToUse = (displayName || "Anonymous").trim();

      // Build a temporary form with hidden inputs and submit
      const form = document.createElement("form");
      form.method = "POST";
      form.action = FORM_ACTION;
      form.target = "_blank";
      form.style.display = "none";

      const addField = (name, value) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value;
        form.appendChild(input);
      };

      addField(ENTRY_MAP.id, generatedId);
      addField(ENTRY_MAP.email, email.trim());
      addField(ENTRY_MAP.pattern_name, titleToUse);
      addField(ENTRY_MAP.creator_name, creatorToUse);
      addField(ENTRY_MAP.image_url, imageDataUrl || "");
      addField(ENTRY_MAP.chart_json, chartJsonBase64);
      addField(ENTRY_MAP.tags, tags.trim());
      const isHadley = email.trim().toLowerCase() === "hadleyswello@gmail.com";
      addField(ENTRY_MAP.approval_status, isHadley ? "approved" : "pending");
      addField(ENTRY_MAP.created_at, today);
      addField(ENTRY_MAP.notes, notes.trim());

      document.body.appendChild(form);
      form.submit();

      // Give the browser a tick to submit
      setTimeout(() => {
        document.body.removeChild(form);
        setIsSubmitting(false);
        onClose(true);
      }, 300);
    } catch (e) {
      setIsSubmitting(false);
      setError("Submission failed. Please try again.");
    }
  }

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Submit to Gallery</DialogTitle>
      <DialogContent>
        <Box className="d-flex flex-column gap-2">
          <Typography variant="body2" color="text.secondary">
            Your pattern will be submitted for review.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Email is optional but is used for follow-ups or removal requests.
          </Typography>

          <TextField
            label="Pattern Name"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Maker's Name (optional)"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Email (optional)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            size="small"
            required={false}
          />
          <TextField
            label="Tags (comma-separated)"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={2}
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => onClose(false)}
          disabled={isSubmitting}
          color="secondary"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
          color="primary"
        >
          {isSubmitting ? "Submitting…" : "Submit Pattern"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubmitToGalleryModal;
